import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star, ChevronRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const VolkswagenService = () => {
  const vwFeatures = [
    'VW Expert Technicians',
    'VCDS/VAG-COM Diagnostics',
    'TDI Clean Diesel Experts',
    'GTI/R Performance Tuning',
    'Genuine VW Parts Available',
    'DSG & Manual Transmission Service'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <>
      <Helmet>
        <title>Volkswagen Repair Dallas-Fort Worth - TDI & DSG Service Experts</title>
        <meta name="description" content="Need VW repair in Dallas? Our certified technicians handle TDI diesels, DSG transmissions and GTI performance. Save 30-40% vs dealership rates. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/volkswagen" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/volkswagen" />
        <meta property="og:title" content="VW Repair & Used Volkswagen Inventory - Dallas-Fort Worth" />
        <meta property="og:description" content="Your Jetta making weird noises? GTI not pulling like it used to? We fix VWs daily and sell quality pre-owned Volkswagens in DFW." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/vw-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VW Repair & Used Volkswagen Inventory - Dallas-Fort Worth" />
        <meta name="twitter:description" content="Your Jetta making weird noises? GTI not pulling like it used to? We fix VWs daily and sell quality pre-owned Volkswagens in DFW." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/vw-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "Volkswagen Service - Paradise-Performance",
            "description": "Expert Volkswagen repair and performance services",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "brand": "Volkswagen"
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does Volkswagen maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Volkswagen maintenance costs average $676 to $900 per year depending on the model. An oil change runs $80-$120 at an independent shop (vs $130-$180 at dealerships). DSG transmission service costs $300-$450. At Paradise-Performance near Dallas, we're typically 30-40% less than VW dealership prices while using VW-spec parts and fluids."
                }
              },
              {
                "@type": "Question",
                "name": "What is the VW EPC light and how do I fix it?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The VW EPC (Electronic Power Control) light indicates a problem with the throttle system, brake system, or traction control. Common causes include throttle body issues, faulty brake light switches, ABS sensor failures, and accelerator pedal sensor problems. Scanning for codes with VCDS or VAG-COM is required to diagnose the specific issue."
                }
              },
              {
                "@type": "Question",
                "name": "How often does a VW DSG transmission need service?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "VW DSG transmissions should be serviced every 40,000 miles. This includes draining and refilling the transmission fluid and replacing the filter. Skipping DSG service can lead to rough shifting, shuddering, and eventual mechatronic unit failure—a $2,000+ repair. Regular service keeps the DSG shifting smoothly for 150,000+ miles."
                }
              },
              {
                "@type": "Question",
                "name": "What are common Volkswagen problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common VW issues include: timing chain tensioner failure (TSI engines, especially 2008-2013), carbon buildup on intake valves (direct injection), DSG mechatronic unit problems, water pump/thermostat failures, PCV valve issues, and ignition coil failures. Most can be prevented with proper maintenance using VW 502.00 or 504.00 spec oil."
                }
              },
              {
                "@type": "Question",
                "name": "Are Volkswagens expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Volkswagens cost slightly more to maintain than domestic brands but less than BMW or Mercedes. Annual maintenance averages $676-$900 at an independent specialist. The key is finding a shop with VCDS diagnostic equipment and VW experience. Using an independent specialist instead of the dealership saves 30-40% on labor and parts."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="Volkswagen"
        brandColor="#001E50"
        tagline="Das Auto - The People's Car Perfected"
        description="Dallas-Fort Worth's premier Volkswagen specialists delivering quality service with German engineering expertise. From TDI diesels to GTI performance, we service all VW models."
        features={vwFeatures}
        heroImage="/assets/assets/images/vw-repair.webp"
        heroImageAlt="Volkswagen Service and Repair"
        heroVariant="default"
      />

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Emergency Promo Box */}
          <motion.div {...fadeInUp} className="bg-blue-50 border-2 border-blue-600 rounded-lg p-6 max-w-4xl mx-auto mb-12">
            <h3 className="text-2xl font-bold text-blue-700 mb-2 text-center">🔧 Volkswagen Expert Technicians Available</h3>
            <p className="text-center text-gray-700 mb-4">
              Schedule your VW service today - Call <strong className="text-blue-600">(817) 382-2316</strong>
            </p>
            <div className="text-center">
              <Link to="/contact" className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition">
                Book VW Service →
              </Link>
            </div>
          </motion.div>

          {/* Introduction Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-6 text-center">Specialized Volkswagen Repair in Dallas-Fort Worth</h1>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div>
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-valve-cover-gasket.webp" 
                  alt="Volkswagen service center in Paradise, TX"
                  className="rounded-lg shadow-lg w-full h-auto"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-2xl font-bold mb-4">Your Trusted VW Service Center</h2>
                <p className="text-gray-700 mb-4">
                  When your Volkswagen needs service, choose Paradise-Performance. Our experienced technicians understand 
                  the unique engineering that makes VW vehicles special - from the efficiency of TDI diesels to the performance of GTI models.
                </p>
                <p className="text-gray-700 mb-4">
                  Located in Paradise, TX, we serve VW owners throughout Dallas-Fort Worth with comprehensive repair, 
                  maintenance, and performance services at prices 30-40% less than dealership rates.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />20+ Years VW Experience</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />Professional VCDS/VAG-COM Tools</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />OEM & Performance Parts</li>
                  <li className="flex items-center"><CheckCircle className="w-5 h-5 text-green-500 mr-2" />12-Month/12,000-Mile Warranty</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Comprehensive Service Grid */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Complete Volkswagen Service Solutions</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Maintenance Services */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Settings className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-bold">VW Maintenance</h3>
                </div>
                <p className="text-gray-700 mb-4">Scheduled service tailored for Dallas driving</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />10K/20K/40K mile services</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Synthetic oil changes (507.00 spec)</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />DSG fluid service (40K miles)</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Brake system service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Coolant system flush</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Multi-point inspection</li>
                </ul>
              </div>

              {/* Repair Services */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Wrench className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-bold">Expert Repairs</h3>
                </div>
                <p className="text-gray-700 mb-4">Professional repairs with genuine VW parts</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Engine diagnostics & repair</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Timing chain/belt service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Water pump replacement</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Turbocharger service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Electrical system repair</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />A/C system service</li>
                </ul>
              </div>

              {/* TDI Diesel Services */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <Car className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-bold">TDI Diesel Experts</h3>
                </div>
                <p className="text-gray-700 mb-4">Specialized clean diesel service & repairs</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />DPF regeneration service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />EGR valve cleaning/repair</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />HPFP (fuel pump) service</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />AdBlue/DEF system repair</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />Emissions compliance</li>
                  <li className="flex items-start"><CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5" />TDI timing belt service</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Common VW Issues */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Volkswagen Issues We Fix</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-10">
              VW vehicles have specific mechanical characteristics that require specialized knowledge. Our technicians 
              are experts in diagnosing and repairing these common VW concerns:
            </p>

            {/* Issue Cards */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-4">
                  <AlertTriangle className="w-8 h-8 text-orange-500 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Carbon Buildup on TSI/TFSI Engines</h3>
                    <p className="text-gray-700 mb-4">
                      Direct injection engines in Golf, GTI, Jetta, and Tiguan models develop carbon deposits on intake valves, 
                      causing rough idle, misfires, and reduced power. Dallas stop-and-go traffic accelerates this issue.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="font-bold text-blue-700 mb-2">Our Solution:</p>
                      <p className="text-gray-700">
                        Professional walnut shell blasting service removes carbon without engine disassembly. We recommend this 
                        service every 40,000-60,000 miles for Dallas drivers to maintain optimal performance.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">DSG Transmission Mechatronic Unit Failures</h3>
                    <p className="text-gray-700 mb-4">
                      VW's dual-clutch DSG transmission can experience mechatronic unit issues, causing harsh shifts, 
                      loss of odd/even gears, or complete transmission failure if not addressed promptly.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="font-bold text-blue-700 mb-2">Our Approach:</p>
                      <p className="text-gray-700">
                        We perform DSG adaptations, software updates, and mechatronic repairs using factory VCDS tools. 
                        Regular 40,000-mile DSG services prevent most issues and extend transmission life significantly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex items-start mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-500 mr-4 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Timing Chain Tensioner Issues (EA888 Engines)</h3>
                    <p className="text-gray-700 mb-4">
                      2012-2015 models with EA888 Gen 2 engines can experience timing chain tensioner failure, potentially 
                      causing catastrophic engine damage. Warning signs include rattling on cold starts and timing codes.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="font-bold text-blue-700 mb-2">Prevention & Repair:</p>
                      <p className="text-gray-700">
                        We inspect timing chains during regular service and recommend preventive replacement at 80,000-100,000 miles. 
                        Updated tensioner designs resolve the issue permanently when installed by our VW specialists.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Models We Service */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Volkswagen Models We Service</h2>
            <div className="bg-gray-100 rounded-lg p-8">
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-blue-600 mb-4">Sedans & Hatches</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Golf, GTI, Golf R</li>
                    <li>• Jetta, GLI</li>
                    <li>• Passat</li>
                    <li>• Arteon</li>
                    <li>• CC</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-600 mb-4">SUVs & Wagons</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Tiguan</li>
                    <li>• Atlas, Atlas Cross Sport</li>
                    <li>• Touareg</li>
                    <li>• Golf Alltrack</li>
                    <li>• Golf SportWagen</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-600 mb-4">Performance & Electric</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Golf R</li>
                    <li>• GTI TCR</li>
                    <li>• ID.4 Electric</li>
                    <li>• e-Golf</li>
                    <li>• Clubsport Models</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-blue-600 mb-4">Classic VWs</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Beetle (All Years)</li>
                    <li>• Bus/Vanagon</li>
                    <li>• Corrado</li>
                    <li>• Scirocco</li>
                    <li>• MK1-MK4 Models</li>
                  </ul>
                </div>
              </div>
              <p className="text-center text-gray-600 mt-6 italic">
                From classic air-cooled Beetles to the latest ID.4 electric, we service all Volkswagen models
              </p>
            </div>
          </motion.div>

          {/* Testimonials */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Dallas VW Owners Say</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "My GTI's DSG was acting up and the dealer wanted $4,800 for a new mechatronic unit. Paradise-Performance 
                  diagnosed it properly, performed a software update and fluid service, and saved me over $3,500. They're the 
                  only shop I trust with my VW now."
                </p>
                <p className="font-bold">— Michael T., GTI MK7 Owner</p>
                <p className="text-sm text-gray-600">Dallas, TX</p>
              </div>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 italic mb-4">
                  "As a TDI owner post-dieselgate, finding a trustworthy shop was crucial. Paradise-Performance handles all 
                  my TDI maintenance and repairs. They understand these engines better than the dealership and charge fair prices."
                </p>
                <p className="font-bold">— Jennifer S., Passat TDI Owner</p>
                <p className="text-sm text-gray-600">Fort Worth, TX</p>
              </div>
            </div>
          </motion.div>

          {/* Comparison Table */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Choose Paradise-Performance?</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-lg">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service Factor</th>
                    <th className="px-6 py-4 text-left">VW Dealership</th>
                    <th className="px-6 py-4 text-left">General Shop</th>
                    <th className="px-6 py-4 text-left">Paradise-Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 font-medium">VCDS/VAG-COM Tools</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4 text-red-600">✗</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">VW-Trained Technicians</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4 text-red-600">✗</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">TDI Diesel Expertise</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4">Limited</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Service Cost</td>
                    <td className="px-6 py-4">$$$$$</td>
                    <td className="px-6 py-4">$$</td>
                    <td className="px-6 py-4 text-green-600">$$$ (30-40% less)</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Warranty</td>
                    <td className="px-6 py-4">12 months</td>
                    <td className="px-6 py-4">30-90 days</td>
                    <td className="px-6 py-4 text-green-600">12 months/12,000 miles</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">How often should I service my VW DSG transmission?</h3>
                <p className="text-gray-700">
                  VW's DSG requires fluid and filter changes every 40,000 miles, despite some claims of "lifetime" fluid. 
                  Dallas heat and stop-and-go traffic make this service critical. Neglecting DSG service leads to expensive 
                  mechatronic failures and premature clutch wear.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Can you service my VW TDI diesel after the emissions fix?</h3>
                <p className="text-gray-700">
                  Absolutely. We're experts in post-fix TDI maintenance and understand the updated software requirements. 
                  We perform DPF regenerations, AdBlue services, and address any drivability concerns that arose from the 
                  emissions modifications.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3">Do you offer performance tuning for GTI and Golf R models?</h3>
                <p className="text-gray-700">
                  Yes, we provide safe, reliable ECU and TCU tuning for all VW performance models. Stage 1-3 tunes, DSG 
                  tuning, and supporting modifications like intakes, exhausts, and intercoolers. All tuning includes proper 
                  logging and custom adjustments for Dallas climate conditions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Cross-link to VW Inventory */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 md:p-10">
              <div className="md:flex items-center gap-8">
                <div className="flex-1 mb-6 md:mb-0">
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">Looking to Buy a Volkswagen?</h2>
                  <p className="text-gray-700">
                    We sell pre-owned VWs inspected by our own technicians. Every vehicle passes the same diagnostic process we use on customer repairs.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link
                    to="/inventory/volkswagen"
                    className="inline-flex items-center gap-2 bg-[#001E50] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#002E70] transition"
                  >
                    Browse VW Inventory <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Other European Vehicles */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Other European Vehicles We Service</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <Car className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Audi</h3>
                <p className="text-sm text-gray-600 mb-3">Quattro & performance experts</p>
                <Link to="/services/european-car-repair/audi" className="text-red-600 hover:underline">Learn More →</Link>
              </div>
              <div className="text-center">
                <Car className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">BMW</h3>
                <p className="text-sm text-gray-600 mb-3">Ultimate driving machine service</p>
                <Link to="/services/european-car-repair/bmw" className="text-blue-600 hover:underline">Learn More →</Link>
              </div>
              <div className="text-center">
                <Car className="w-12 h-12 text-gray-800 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Mercedes-Benz</h3>
                <p className="text-sm text-gray-600 mb-3">Luxury specialists</p>
                <Link to="/services/european-car-repair/mercedes" className="text-gray-800 hover:underline">Learn More →</Link>
              </div>
              <div className="text-center">
                <Car className="w-12 h-12 text-red-700 mx-auto mb-3" />
                <h3 className="font-bold text-lg mb-2">Porsche</h3>
                <p className="text-sm text-gray-600 mb-3">Performance focused</p>
                <Link to="/services/european-car-repair/porsche" className="text-red-700 hover:underline">Learn More →</Link>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-12 text-white">
            <h2 className="text-4xl font-bold mb-4">Schedule Your Volkswagen Service Today</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Experience the difference of specialized VW service. Our expert technicians deliver 
              dealer-quality work at prices that make sense for Dallas-Fort Worth drivers.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
              >
                Schedule VW Service
              </Link>
              <a
                href="tel:8173822316"
                className="bg-black text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-900 transition"
              >
                Call (817) 382-2316
              </a>
            </div>
            <p className="mt-6 text-lg">Serving Dallas-Fort Worth from Paradise, TX</p>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default VolkswagenService;
